import PageLayout from "components/PageLayout";
import FAQItem from "components/FAQItem";

const FAQ = () => {
  return (
    <PageLayout pageTitle={"Frequently Asked Questions"}>
        
        <FAQItem question="How does ThermalPoint work?">
            <p>
                <p>ThermalPoint starts by creating a simple model of how a home’s heating energy 
                    use varies with outdoor temperature—essentially an estimate of the home’s Heating 
                    Load Line.
                </p><br></br>
                <p>It then applies this model to weather data for the home’s location to estimate total 
                    natural gas consumption, adjusting for other natural gas appliances in the home. 
                    If the estimate does not match the actual natural gas consumption, the model is 
                    adjusted until it aligns.
                </p><br></br>
                <p>The resulting Heating Load Line is then used to estimate the home’s heat loss at the 
                    outdoor design temperature for the selected location.</p>
            </p>
        </FAQItem>
        <FAQItem question="Why do I need to enter information about my home (type, age, and upgrades)?">
            <p>
                <p>ThermalPoint estimates the outdoor temperature at which a home begins to require 
                    heating as part of its calculation.</p>
                <br></br>
                <p>This estimate is based on the home’s type, age, and level of energy upgrades, which 
                    influence how much heat the home loses and how early heating is required.</p>
                <br></br>
                <p>For example, an older detached home with minimal upgrades may begin requiring heating 
                    at around 16 °C, while a newer or more efficient home may not require heating until 
                    a lower outdoor temperature. Homes that share walls (e.g. townhouses) also tend to 
                    require heating later due to reduced heat loss.</p>
                <br></br>
                <p>Typical values used in the model range from approximately 8 °C for highly efficient 
                    attached homes to 16 °C for older detached homes.
                </p>
            </p>
        </FAQItem>
        <FAQItem question="Why do you ask for indoor temperature?">
            <p>
                <p>Indoor temperature affects how much heating a home requires. Homes maintained at 
                    lower temperatures require less heating and will have a lower balance point 
                    temperature.</p>
                <br></br>
                <p>If the thermostat is set back at certain times (e.g. overnight), enter the average 
                    indoor temperature over a full day. This provides a reasonable approximation of typical 
                    conditions.</p>
                <br></br>
                <p>If unsure, an approximate average is sufficient.</p>
            </p>
        </FAQItem>
        <FAQItem question="How does ThermalPoint account for other natural gas appliances in the home?">
            <p>
                <p>The user estimates the monthly natural gas use for appliances unrelated to space heating, 
                    such as a dryer, stove, water heater, and/or barbecue.</p>
                <br></br>
                <p>ThermalPoint subtracts this estimated usage from the home’s total natural gas consumption 
                    to isolate the energy used for space heating, which is used in the calculation.</p>
            </p>
        </FAQItem>
        <FAQItem question="Why doesn’t ThermalPoint work in homes with certain gas appliances?">
            <p>
                <p>ThermalPoint can adjust for smaller appliances like dryers, stoves, water heaters, and 
                    barbecues because their natural gas consumption is relatively small compared to the 
                    furnace, so estimation errors have limited impact.</p>
                <br></br>
                <p>However, larger or highly variable loads—such as pool heaters, gas-heated saunas, 
                    hot tubs, or outbuildings—can consume as much or more natural gas than the furnace. 
                    These loads make it difficult to separate space heating from other uses, which reduces 
                    accuracy.</p>
            </p>
        </FAQItem>
        <FAQItem question="What if natural gas only heats certain zones of the home?">
            <p>
                <p>ThermalPoint estimates the heating demand of the zones served by the natural gas system.</p>
                <br></br>
                <p>If natural gas heats only certain zones of the home (e.g. a furnace serving the main and 
                    second floors, with a third floor served by an electric heat pump), the results will reflect 
                    only the zones served by the natural gas system.</p>
                <br></br>
                <p>Care should be taken when interpreting results in these cases, particularly when comparing to 
                    total home heating requirements.</p>
            </p>
        </FAQItem>
        <FAQItem question="What if there are other heating sources (e.g. heat pump, wood stove)?">
            <p>
                <p>ThermalPoint assumes that natural gas provides nearly all of the space heating.</p>
                <br></br>
                <p>Small or infrequently used supplemental heating sources (e.g. occasional use of a heat 
                    pump or wood stove) may have a limited impact on results.</p>
                <br></br>
                <p>However, if another system provides a significant portion of the heating, the results 
                    may be inaccurate.</p>
            </p>
        </FAQItem>
        <FAQItem question="What if the home has changed (e.g. addition or renovation)?">
            <p>
                <p>ThermalPoint assumes that the gas consumption data reflects how the home currently operates.</p>
                <br></br>
                <p>If energy upgrades were made after the data was collected (e.g. added insulation or improved air 
                    sealing), the results may be conservative and overestimate the home’s heating requirements.</p>
                <br></br>
                <p>If the home was enlarged or otherwise modified in a way that increases heat loss (e.g. an addition), 
                    the results may underestimate the current heating requirements, which could lead to undersizing of 
                    equipment.</p>
                <br></br>
                <p>In these cases, results should be interpreted with caution.</p>
            </p>
        </FAQItem>
        <FAQItem question="How does a natural gas fireplace impact the results?">
            <p>
                <p>ThermalPoint generally works in homes with a natural gas fireplace, but it may lead to more conservative 
                    results (higher estimated heat loss and a warmer balance point).</p>
                <br></br>
                <p>This is because ThermalPoint assumes space heating is delivered at the furnace’s efficiency, while 
                    fireplaces are typically less efficient. This can result in a slight overestimate of delivered heat.</p>
            </p>
        </FAQItem>
        <FAQItem question="How is ThermalPoint different from other approaches?">
            <p>
                <p>ThermalPoint takes an empirical approach, estimating heating behaviour based on actual energy consumption.</p>
                <br></br>
                <p>In contrast, traditional Design Heat Loss (DHL) calculations rely on detailed models of the building envelope, 
                    including insulation levels, dimensions, and air leakage. These inputs can be difficult to obtain for existing 
                    homes without extensive site assessment.</p>
                <br></br>
                <p>DHL calculations are also based on worst-case conditions and typically exclude internal and solar gains, leading 
                    to more conservative sizing. ThermalPoint reflects typical operating conditions more directly, which can provide 
                    a more realistic estimate of average heating demand.</p>
            </p>
        </FAQItem>
        <FAQItem question="What should I assume for the safety margin?">
            <p>
                <p>The safety margin is a multiplying factor applied to the estimated heat loss to provide a more conservative result.</p>
                <br></br>
                <p>If you prefer a more conservative estimate, a value of up to approximately 25% is commonly used. Higher values 
                    increase the estimated heating requirement and reduce the risk of undersizing, but may lead to oversizing.</p>
            </p>
        </FAQItem>
        <FAQItem question="What are good values for thermal balance point temperature and heating fraction?">
            <p>
                <p>There are no single “correct” values. These outputs help inform heat pump sizing decisions based on the homeowner’s 
                    goals and system design.</p>
                <br></br>
                <p>If the goal is for the heat pump to provide most or all of the heating, the balance point temperature should be 
                    close to the outdoor design temperature, and the heating fraction should be high.</p>
                <br></br>
                <p>However, a heat pump can still provide a large share of annual heating even if the balance point is several degrees 
                    warmer than the design temperature.</p>
                <br></br>
                <p>For example, in Toronto (design temperature −18 °C):</p>
                <ul>
                    <li>At −5 °C → ~75% of annual heating</li>
                    <li>At −10 °C → ~90% of annual heating</li>
                </ul>
            </p>
        </FAQItem>
        <FAQItem question="How accurate is ThermalPoint?">
            <p>
                <p>ThermalPoint provides an estimate of a home’s heating behaviour based on actual natural gas consumption.</p>
                <br></br>
                <p>Early case studies and field experience suggest that empirical methods can align well with real-world heat loss 
                    and often produce lower values than traditional design heat loss calculations.</p>
                <br></br>
                <p>For a detailed discussion of the validation work and case studies, see:
                    {" "}
                  <a
                  href="https://plumbingandhvac.ca/expanding-the-toolkit-part-2/"
                  target="_blank"
                  rel="noreferrer"
                >
                  “Expanding the Toolkit Part 2” (Plumbing & HVAC Magazine).
                </a>{" "}</p>
                <br></br>
                <p>Results depend on the quality of the input data and are intended to support heat pump sizing 
                    decisions, not replace formal heat loss calculations where required.</p>
            </p>
        </FAQItem>
        <FAQItem question="How can this tool help my calculations in NRCan’s Air-Source Heat Pump Sizing and Selection Web Application?">
            <p>
                <p>When using NRCan’s Web App, you can leave heat pump capacity inputs at zero in ThermalPoint.</p>
                <br></br>
                <p>Use the “Estimated average heat loss at outdoor design temperature” from ThermalPoint as 
                    the Design Heat Loss input in the NRCan Web App.</p>
                <br></br>
                <p>Note that ThermalPoint does not perform a CSA F280-compliant heat loss calculation.</p>
            </p>
        </FAQItem>
    </PageLayout>
  );
};

export default FAQ;